import React, { useState } from "react";
import {
  Phone,
  MessageCircle,
  CheckCircle2,
  Menu,
  X,
  Droplets,
  Waves,
  Trash2,
  Filter,
  Recycle,
  Gauge,
  Wrench,
  Truck,
  ShieldCheck,
  Users,
  BadgeCheck,
  Award,
  MapPin,
  Clock,
} from "lucide-react";
import {
  BRAND,
  PHONE_DISPLAY,
  TEL_LINK,
  WHATSAPP_RAW,
  trackLeadConversion,
} from "@/constants/contact";

/* ---------- CONTACT LINKS ---------- */
const SEPTIC_WA_MSG = encodeURIComponent(
  "Hi ABL Experts, I am looking for septic tank cleaning service in visakhapatnam"
);
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_RAW}?text=${SEPTIC_WA_MSG}`;

/* ---------- DATA ---------- */
const heroImage = "/septic-hero.png";
const bandImage = "/septic-band.png";

const services = [
  { icon: Droplets, title: "Septic Tank Cleaning" },
  { icon: Waves, title: "Sewage Tank Cleaning" },
  { icon: Trash2, title: "Soak Pit Cleaning" },
  { icon: Filter, title: "Drainage Cleaning" },
  { icon: Recycle, title: "Manhole Cleaning" },
  { icon: Gauge, title: "Sewer Line Jetting" },
  { icon: Wrench, title: "Grease Trap Cleaning" },
  { icon: Truck, title: "Tank Desludging" },
];

const areas = [
  "Bheemunipatnam",
  "Tagarapuvalasa",
  "Marripalem",
  "NSTL",
  "Arilova",
  "Gajuwaka",
  "Pendurthi",
  "Rushikonda",
  "Madhurawada",
  "Simhachalam",
  "Anandapuram",
  "Car Shed",
  "Yendada",
  "MVP Colony",
  "Siripuram",
  "All over Visakhapatnam",
];

/* ---------- COMPONENTS ---------- */
const ServiceIcon = ({ Icon, label }) => (
  <div
    className="flex flex-col items-center rounded-xl border border-slate-200 bg-white p-3 transition hover:-translate-y-0.5 hover:border-[#0b2a59] hover:shadow-md sm:p-4"
    data-testid={`service-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="grid h-12 w-12 place-items-center text-[#0b2a59] sm:h-14 sm:w-14">
      <Icon strokeWidth={1.5} className="h-7 w-7 sm:h-8 sm:w-8" />
    </div>
    <div className="mt-2 text-center text-[11px] font-semibold text-slate-700 sm:text-sm">
      {label}
    </div>
  </div>
);

const LandingPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navLinks = [
    { label: "Home", href: "#top" },
    { label: "Services", href: "#services" },
    { label: "Areas", href: "#areas" },
  ];

  return (
    <div
      className="min-h-screen bg-white text-slate-900"
      data-testid="mas-landing-page"
    >
      {/* HEADER */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white">
        <div className="relative mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-lg p-2 text-slate-700 hover:bg-slate-100"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>

          {/* Centered brand */}
          <a
            href="#top"
            className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2.5"
            data-testid="brand-logo"
          >
            <img
              src="/logo.jpg"
              alt="ABL Experts logo"
              className="h-11 w-11 rounded-full object-cover ring-2 ring-[#0b2a59]/10 sm:h-12 sm:w-12"
            />
            <div className="leading-tight">
              <div className="font-display text-base font-extrabold tracking-tight sm:text-xl">
                <span className="text-[#0b2a59]">ABL</span> <span className="text-[#e02424]">Experts</span>
              </div>
              <div className="text-[8px] font-bold uppercase tracking-[0.16em] text-slate-600 sm:text-[10px]">
                Septic Tank Cleaning
              </div>
            </div>
          </a>

          <a
            href={TEL_LINK}
            onClick={trackLeadConversion}
            className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] font-semibold text-[#0b2a59] shadow-sm transition hover:border-[#0b2a59] sm:px-3 sm:py-2 sm:text-xs"
            data-testid="header-call-now"
          >
            <Phone className="mb-0.5 h-4 w-4 text-[#0b2a59] sm:h-5 sm:w-5" />
            <span>Call Now</span>
          </a>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-200 bg-white">
            <div className="mx-auto flex max-w-5xl flex-col px-4 py-1">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className="border-b border-slate-100 py-3 text-sm font-semibold text-slate-700 last:border-b-0"
                  data-testid={`menu-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section
        id="top"
        className="relative overflow-hidden bg-gradient-to-b from-slate-100 to-slate-50"
      >
        <div className="mx-auto max-w-5xl px-4 pb-5 pt-6 sm:pt-8">
          <div className="grid grid-cols-12 gap-2 sm:gap-6">
            {/* Left: copy */}
            <div className="col-span-7">
              <h1
                className="font-display text-[20px] font-extrabold uppercase leading-[1.05] tracking-tight text-[#0b2a59] sm:text-4xl md:text-5xl"
                data-testid="hero-heading"
              >
                Septic Tank Cleaning
                <br />
                <span className="text-[#0b2a59]">Service in Visakhapatnam</span>
              </h1>
              <p className="mt-2 text-xs font-medium leading-tight text-slate-700 sm:mt-3 sm:text-lg">
                Fast &amp; Hygienic Service
                <br />
                at Your Doorstep
              </p>
              <ul className="mt-3 space-y-1.5 sm:mt-5 sm:space-y-2.5">
                {[
                  "Same Day Service",
                  "Trained & Equipped Team",
                  "Modern Vacuum Machines",
                  "Safe & Hygienic Cleaning",
                  "Trusted Experts",
                ].map((b) => (
                  <li
                    key={b}
                    className="flex items-center gap-1.5 text-[11px] font-medium leading-tight text-slate-800 sm:gap-2.5 sm:text-base"
                  >
                    <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-[#0b2a59] sm:h-6 sm:w-6">
                      <CheckCircle2
                        className="h-2.5 w-2.5 fill-[#0b2a59] text-white sm:h-3.5 sm:w-3.5"
                        strokeWidth={3}
                      />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Hero image - worker with vacuum tanker */}
            <div
              className="col-span-5 self-end"
              data-testid="hero-image"
            >
              <div className="relative h-[210px] w-full overflow-hidden rounded-xl sm:h-[340px]">
                <img
                  src={heroImage}
                  alt="ABL Experts septic tank cleaning technician with a vacuum tanker in Visakhapatnam"
                  loading="eager"
                  className="absolute inset-0 h-full w-full object-contain object-bottom"
                />
              </div>
            </div>
          </div>

          {/* CTAs - small, side-by-side */}
          <div
            className="mt-4 grid grid-cols-2 gap-2 sm:mt-5 sm:gap-3"
            data-testid="hero-ctas"
          >
            <a
              href={TEL_LINK}
              onClick={trackLeadConversion}
              className="flex items-center justify-center gap-2 rounded-lg bg-[#0b2a59] px-3 py-2.5 text-white shadow-sm transition hover:brightness-110 sm:py-3"
              data-testid="hero-call-button"
            >
              <Phone className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
              <div className="text-left leading-tight">
                <div className="text-xs font-extrabold uppercase tracking-wide sm:text-sm">
                  Call Now
                </div>
                <div className="text-[9px] font-semibold tracking-wide opacity-90 sm:text-[10px]">
                  {PHONE_DISPLAY}
                </div>
              </div>
            </a>
            <a
              href={WHATSAPP_LINK}
              onClick={trackLeadConversion}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-lg bg-[#16a34a] px-3 py-2.5 text-white shadow-sm transition hover:brightness-110 sm:py-3"
              data-testid="hero-whatsapp-button"
            >
              <MessageCircle className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
              <div className="text-left leading-tight">
                <div className="text-xs font-extrabold uppercase tracking-wide sm:text-sm">
                  WhatsApp Now
                </div>
                <div className="text-[9px] font-semibold tracking-wide opacity-90 sm:text-[10px]">
                  Quick Response
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="px-4 py-5 sm:py-7">
        <div className="mx-auto grid max-w-5xl grid-cols-4 gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:gap-4 sm:p-5">
          {[
            { icon: Users, value: "12+", label: ["Years", "Experience"] },
            {
              icon: BadgeCheck,
              value: "10,000+",
              label: ["Tanks", "Cleaned"],
            },
            { icon: Award, value: "Trusted", label: ["Badge", ""] },
            {
              icon: ShieldCheck,
              value: "100%",
              label: ["Safe &", "Hygienic"],
            },
          ].map((s, i) => (
            <div
              key={i}
              className="flex flex-col items-center text-center"
              data-testid={`stat-${i}`}
            >
              <span className="mb-1 text-slate-400">
                <s.icon strokeWidth={1.5} className="h-5 w-5 sm:h-6 sm:w-6" />
              </span>
              <div className="font-display text-base font-extrabold leading-tight text-[#0b2a59] sm:text-2xl">
                {s.value}
              </div>
              <div className="mt-0.5 text-[10px] font-medium leading-tight text-slate-600 sm:text-xs">
                {s.label[0]}
                <br />
                {s.label[1]}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* OUR SERVICES */}
      <section id="services" className="px-4 py-8 sm:py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center font-display text-lg font-extrabold uppercase tracking-wide text-[#0b2a59] sm:text-2xl">
            Our Services
          </h2>
          <div className="mt-5 grid grid-cols-4 gap-2 sm:gap-4">
            {services.map((s, i) => (
              <ServiceIcon key={`${s.title}-${i}`} Icon={s.icon} label={s.title} />
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE BAND */}
      <section className="px-4 py-6 sm:py-10" data-testid="about-band">
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm md:grid-cols-2">
          <div className="relative min-h-[200px] w-full sm:min-h-[260px]">
            <img
              src={bandImage}
              alt="ABL Experts workers operating a vacuum tanker for septic tank cleaning in Visakhapatnam"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
          <div className="p-5 sm:p-8">
            <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-[#0b2a59] sm:text-2xl">
              Modern Equipment, Trained Team
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
              We use modern vacuum tankers and high-pressure jetting machines to clean septic tanks, soak pits and drainage lines quickly and hygienically. Our trained team handles residential, commercial and apartment complexes across Visakhapatnam.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-2 sm:max-w-md">
              <a
                href={TEL_LINK}
                onClick={trackLeadConversion}
                className="flex items-center justify-center gap-2 rounded-lg bg-[#0b2a59] px-3 py-2.5 text-white shadow-sm transition hover:brightness-110"
                data-testid="band-call-button"
              >
                <Phone className="h-4 w-4" />
                <span className="text-xs font-extrabold uppercase tracking-wide">Call Now</span>
              </a>
              <a
                href={WHATSAPP_LINK}
                onClick={trackLeadConversion}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 rounded-lg bg-[#16a34a] px-3 py-2.5 text-white shadow-sm transition hover:brightness-110"
                data-testid="band-whatsapp-button"
              >
                <MessageCircle className="h-4 w-4" />
                <span className="text-xs font-extrabold uppercase tracking-wide">WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section
        id="areas"
        className="px-4 pb-4 pt-8 sm:pb-6 sm:pt-12"
        data-testid="areas-section"
      >
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center font-display text-lg font-extrabold uppercase tracking-wide text-[#0b2a59] sm:text-2xl">
            Service Areas in Visakhapatnam
          </h2>
          <div className="mt-5 grid grid-cols-2 gap-y-2 gap-x-3 text-[12px] font-medium text-slate-800 sm:grid-cols-3 sm:gap-x-6 sm:text-sm">
            {areas.map((a) => (
              <div
                key={a}
                className="flex items-center gap-1.5"
                data-testid={`area-${a.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <CheckCircle2
                  className="h-4 w-4 fill-[#16a34a] text-white"
                  strokeWidth={3}
                />
                <span>{a}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#082147] px-4 pb-24 pt-6 text-slate-300 sm:px-6 sm:pb-10 sm:pt-8" data-testid="site-footer">
        <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/logo.jpg"
                alt="ABL Experts logo"
                className="h-14 w-14 rounded-full object-cover ring-2 ring-white/20"
              />
              <div className="font-display text-lg font-extrabold tracking-tight text-white">
ABL <span className="text-[#e02424]">Experts</span>
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-400">
              Visakhapatnam&apos;s trusted septic tank cleaning service. Fast, hygienic doorstep cleaning with modern vacuum machines and a trained team.
            </p>
          </div>

          <div>
            <div className="font-display text-sm font-extrabold uppercase tracking-wider text-white">
              Contact
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <a
                  href={TEL_LINK}
                  onClick={trackLeadConversion}
                  className="inline-flex items-center gap-2 transition hover:text-white"
                  data-testid="footer-phone-link"
                >
                  <Phone className="h-4 w-4 text-[#16a34a]" /> {PHONE_DISPLAY}
                </a>
              </li>
              <li>
                <a
                  href={WHATSAPP_LINK}
                  onClick={trackLeadConversion}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 transition hover:text-white"
                  data-testid="footer-whatsapp-link"
                >
                  <MessageCircle className="h-4 w-4 text-[#16a34a]" /> WhatsApp {PHONE_DISPLAY}
                </a>
              </li>
              <li className="inline-flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#16a34a]" /> Visakhapatnam, Andhra Pradesh, India
              </li>
              <li className="inline-flex items-center gap-2">
                <Clock className="h-4 w-4 text-[#16a34a]" /> Open Mon - Sun
              </li>
            </ul>
          </div>

          <div>
            <div className="font-display text-sm font-extrabold uppercase tracking-wider text-white">
              Services
            </div>
            <ul className="mt-3 grid grid-cols-2 gap-2 text-sm">
              {services.map((s, i) => (
                <li key={`${s.title}-f-${i}`} className="text-slate-400">
                  {s.title}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-7xl border-t border-white/10 pt-6 text-center text-xs text-slate-400 sm:text-sm">
          © {new Date().getFullYear()} {BRAND}. All rights reserved.
        </div>
      </footer>

      {/* STICKY BOTTOM CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 grid grid-cols-2 shadow-[0_-10px_30px_-10px_rgba(15,23,42,0.25)]"
        data-testid="sticky-cta-bar"
      >
        <a
          href={TEL_LINK}
          onClick={trackLeadConversion}
          className="flex items-center justify-center gap-2.5 bg-[#0b2a59] px-3 py-3.5 text-white"
          data-testid="sticky-call-button"
        >
          <Phone className="h-5 w-5" />
          <div className="text-left leading-tight">
            <div className="text-sm font-extrabold uppercase tracking-wide">
              Call Now
            </div>
            <div className="text-[10px] font-semibold opacity-90">
              {PHONE_DISPLAY}
            </div>
          </div>
        </a>
        <a
          href={WHATSAPP_LINK}
          onClick={trackLeadConversion}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2.5 bg-[#16a34a] px-3 py-3.5 text-white"
          data-testid="sticky-whatsapp-button"
        >
          <MessageCircle className="h-5 w-5" />
          <div className="text-left leading-tight">
            <div className="text-sm font-extrabold uppercase tracking-wide">
              WhatsApp Now
            </div>
            <div className="text-[10px] font-semibold opacity-90">
              Quick Response
            </div>
          </div>
        </a>
      </div>

      {/* Minimal SR-only brand for SEO */}
      <span className="sr-only">{BRAND} — Septic Tank Cleaning Service in Visakhapatnam</span>
    </div>
  );
};

export default LandingPage;
